import { Inject, Injectable, OnDestroy } from '@angular/core'
import { MsalBroadcastService, MsalService } from '@azure/msal-angular'
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  IdTokenClaims,
  InteractionStatus,
} from '@azure/msal-browser'
import { Subject, filter, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from '../models'
import { DefaultAadPermissions, Permissions } from '../enums/permissions.enum'
import { Datasources } from 'src/app/shared/enums/datasources.enum'

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string
  tfp?: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  _destroying$ = new Subject<void>()
  loginDisplay = false
  public activeAccountChanges = new Subject<AccountInfo>()
  private MsalInitialized: boolean = false

  constructor(
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        let payload = result.payload as AuthenticationResult
        let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId

        if (
          idtoken.acr === this.config.aadb2c_policy ||
          idtoken.tfp === this.config.aadb2c_policy
        ) {
          this.msalService.instance.setActiveAccount(payload.account)
          if (payload.account) {
            this.activeAccountChanges.next(payload.account)
          }
        }

        return result
      })

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$),
      )
      .subscribe(async (result: EventMessage) => {
        this.logout()
      })

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay()
        this.checkAndSetActiveAccount()
      })
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined)
    this._destroying$.complete()
  }

  private setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0
  }

  public msalIsInitialized(): boolean {
    return this.MsalInitialized
  }

  private checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalService.instance.getActiveAccount()

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts()
      this.msalService.instance.setActiveAccount(accounts[0])
      this.activeAccountChanges.next(accounts[0])
    }
  }

  public logout(): void {
    this.msalService.logoutRedirect()
    this.MsalInitialized = false
  }

  public getAccountInfo(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount()
  }

  public getUserClaims(): {
    userPermissions: Permissions[]
    userDatasources: Datasources[]
    userBackendScopes: string[]
  } {
    let userPermissions: Permissions[] = []
    let userDatasources: Datasources[] = []
    let userBackendScopes: string[] = []

    let tokenPermissions =
      (this.msalService.instance.getActiveAccount()?.idTokenClaims?.[
        `permissions`
      ] as Permissions[]) || []

    let tokenDataFilters = (
      (this.msalService.instance.getActiveAccount()?.idTokenClaims?.[
        `dataFilters`
      ] as Datasources[]) || []
    ).filter((filter) => filter.includes('signs'))

    let tokenBackendScopes =
      (this.msalService.instance.getActiveAccount()?.idTokenClaims?.[`scopes`] as string[]) || []

    // has aad idp
    if (
      this.msalService.instance.getActiveAccount()?.idTokenClaims?.idp ===
      this.config.bosch_aad_idp_id
    ) {
      // has aad idp and no vzo permissions
      if (
        !tokenPermissions.some(
          (permission) => permission.startsWith('vzo') && permission !== 'vzo.admin',
        )
      ) {
        tokenPermissions = DefaultAadPermissions
        // set default data filter for BoschInternal
        tokenDataFilters.push(Datasources.Vzo)
      }
    }

    // has no aad idp and no permissions
    // has aad idp and permissions
    // has no aad idp and permissions

    userPermissions = tokenPermissions
    userDatasources = tokenDataFilters
    userBackendScopes = tokenBackendScopes

    if (
      !userPermissions.some(
        (permission) => permission.startsWith('vzo') && permission !== 'vzo.admin',
      )
    ) {
      console.warn('User has no VZO permissions, logging out for security reasons.')
      this.msalService.instance.clearCache().then(() => this.logout())
    }
    return { userPermissions, userDatasources, userBackendScopes }
  }

  public hasPermission(permissions: Permissions[], match: 'all' | 'any' = 'any'): boolean {
    const userPermissions = this.getUserClaims().userPermissions
    if (match === 'all') {
      return permissions.every((permission) => userPermissions.includes(permission))
    } else {
      return permissions.some((permission) => userPermissions.includes(permission))
    }
  }

  public async getAccessToken() {
    const authResult = await this.msalService.instance.acquireTokenSilent({
      scopes: [this.config.scope],
    })
    return authResult.accessToken
  }

  public async InitializeMsal() {
    try {
      await this.msalService.instance.initialize()
      this.MsalInitialized = true
      const accounts = this.msalService.instance.getAllAccounts()
      if (accounts.length > 0) {
        const accessTokenRequest = { scopes: [this.config.scope], account: accounts[0] }
        await this.msalService.instance.acquireTokenSilent(accessTokenRequest)
      }
    } catch (e) {
      // if it's not possible to acquire a token we log the user out.    this.logout()  }}
    }
  }
}
